import React from "react";
import CustomTypography from "../common/form/CustomTypography";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import { OrdersList } from "../../../models/Dashboard/OpsDashboard/OpsAnalytics";

interface DashboardOrderTableProps {
  orders: OrdersList[];
  maxHeight?: number;
  minHeight?: number;
  limit?: number;
  page_no?: number;
  total_pages?: number;
  total_count?: number;
  onPageChange: (page: number) => void;
  sortBy?: string;
  sortType?: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
  onRowsPerPageChange: (limit: number) => void;
}

const DashboardOrderTable: React.FC<DashboardOrderTableProps> = (
  props: DashboardOrderTableProps
) => {
  const handleChangePage = (event: unknown, newPage: number) => {
    props.onPageChange(newPage);
  };
  const theme = useTheme();
  const handleRowsChange = (value: number) => {
    props.onPageChange(1);
    props.onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (props.sortBy === property) {
      return props.sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <Box sx={{ marginTop: 4 }}>
      <Box
        sx={{
          backgroundColor: theme.palette.customColors.white,
          padding: 4,
          borderRadius: 2,
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <CustomTypography variant="heading4">Projects</CustomTypography>
        </Box>
        {props.orders.length > 0 ? (
          <Box>
            <TableContainer sx={{ maxHeight: props.maxHeight }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <AppTableHeaderCell
                      sx={{
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        fontWeight: "bold",
                      }}
                    >
                      <TableSortLabel
                        active={props.sortBy === "id"}
                        direction={
                          props.sortType === SortType.ASC ? "asc" : "desc"
                        }
                        onClick={() =>
                          props.onSortChange(
                            "id",
                            props.sortBy === "id" &&
                              props.sortType === SortType.DESC
                              ? SortType.ASC
                              : SortType.DESC
                          )
                        }
                        IconComponent={() => (
                          <span>{renderSortIcon("id")}</span>
                        )}
                        sx={{ fontSize: "12px" }}
                      >
                        ORDER ID
                      </TableSortLabel>
                    </AppTableHeaderCell>
                    <AppTableHeaderCell
                      minWidth="200px"
                      sx={{
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        fontWeight: "bold",
                        maxWidth: 400,
                      }}
                    >
                      PACKS
                    </AppTableHeaderCell>
                    <AppTableHeaderCell
                      minWidth="200px"
                      sx={{
                        color: "#93969B",
                        fontWeight: "bold",
                        maxWidth: 400,
                      }}
                    >
                      ORDER TYPE
                    </AppTableHeaderCell>
                    <AppTableHeaderCell
                      sx={{
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        fontWeight: "bold",
                      }}
                    >
                      SHIPMENTS
                    </AppTableHeaderCell>
                    <AppTableHeaderCell
                      sx={{
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        fontWeight: "bold",
                      }}
                    >
                      PACKING OPS
                    </AppTableHeaderCell>
                    <AppTableHeaderCell
                      sx={{
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        fontWeight: "bold",
                      }}
                    >
                      DISPATCH OPS
                    </AppTableHeaderCell>

                    <AppTableHeaderCell
                      sx={{
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        fontWeight: "bold",
                      }}
                    >
                      TRACKING UPDATED
                    </AppTableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {props.orders.map((row) => (
                    <TableRow key={row.id}>
                      <AppTableRowCell
                        sx={{
                          color: theme.palette.customColors.purple,
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        {row.id}
                      </AppTableRowCell>
                      <AppTableRowCell>
                        {row.packs_count == 0 ? "-" : row.packs_count}
                      </AppTableRowCell>
                      <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                        <span
                          style={{
                            backgroundColor:
                              row.order_type === "Storage"
                                ? theme.palette.mintGreen.light
                                : row.order_type === "Unpaid Sample"
                                ? theme.palette.customColors.greyBodyBackground
                                : row.order_type === "Paid Sample"
                                ? theme.palette.green.light
                                : row.order_type === "Hybrid"
                                ? theme.palette.brown.light
                                : row.order_type === "Event"
                                ? theme.palette.blushPink.light
                                : row.order_type === "Normal"
                                ? theme.palette.purple.light
                                : "transparent",
                            borderRadius: "17px",
                            padding: "3px 3px",
                            display: "inline-flex",
                            justifyContent: "center",
                            minWidth: "120px",
                            color: theme.palette.mode === "dark" ?  theme.palette.customColors.blackBodyBackgorund : theme.palette.customColors.blackBodyBackgorund,
                          }}
                        >
                          {row.order_type}
                        </span>
                      </AppTableRowCell>

                      <AppTableRowCell>
                        {row.shipments_count === 0 ? "-" : row.shipments_count}
                      </AppTableRowCell>

                      <AppTableRowCell>
                        <span
                          style={{
                            backgroundColor: row.packing_done
                              ? theme.palette.blue.main
                              : !row.packing_done
                              ? theme.palette.customColors.lightred
                              : "transparent",
                            borderRadius: "50%",
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            alignItems: "end",
                            marginRight: "8px",
                          }}
                        ></span>
                        {row.packing_done ? "Yes" : "No"}
                      </AppTableRowCell>

                      <AppTableRowCell>
                        <span
                          style={{
                            backgroundColor: row.dispatch_done
                              ? theme.palette.blue.main
                              : !row.dispatch_done
                              ? theme.palette.customColors.lightred
                              : "transparent",
                            borderRadius: "50%",
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            alignItems: "end",
                            marginRight: "8px",
                          }}
                        ></span>
                        {row.dispatch_done ? "Yes" : "No"}
                      </AppTableRowCell>

                      <AppTableRowCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <span>
                            <Box
                              sx={{
                                width: "100px",
                                height: "9px",
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                                borderRadius: "5px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${row.shipments_tracking_percentage}%`,
                                  height: "100%",

                                  backgroundColor:
                                    row.shipments_tracking_percentage >= 80
                                      ? theme.palette.green.main
                                      : row.shipments_tracking_percentage >= 60
                                      ? theme.palette.blue.main
                                      : row.shipments_tracking_percentage >= 40
                                      ? theme.palette.goldenYellow.main
                                      : theme.palette.customColors.lightred,
                                  borderRadius: "5px",
                                }}
                              ></Box>
                            </Box>{" "}
                          </span>{" "}
                          {row.shipments_tracking_percentage}%{" "}
                        </Box>
                      </AppTableRowCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <AppTablePagenation
              fontSize="0.8rem"
              pageNo={props.page_no || 1}
              limit={props.limit || 0}
              totalCount={props.total_count || 0}
              onPageChange={handleChangePage}
              handleRowsChange={handleRowsChange}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              height: "50px",
            }}
          >
            No Data available
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DashboardOrderTable;
